import { Car } from 'lucide-preact';
import { route } from 'preact-router';
import { formatMinor } from '../../utils/money.js';
import { type Promo, promoChip } from '../../utils/promos.js';
import type { TripWindow } from '../../utils/tripQuery.js';
import { tripQueryString } from '../../utils/tripQuery.js';
import { Card } from './Card.js';
import { type CarFuel, FuelGauge } from './FuelGauge.js';

interface CarCardProps {
  /** 126: whatever pickup/return window SearchResultsPage is currently filtering by,
   * forwarded onto the car page instead of dropped. */
  searchWindow?: TripWindow;
  car: {
    _id: string;
    name: string;
    /** 052: the display label. Preferred over `name`, which is a `·`-joined
     * specification dump that also goes stale after an admin edit. */
    label?: string;
    transmission: string;
    fuel_type: string;
    seat_capacity: number;
    images?: Array<{ url: string; is_primary?: boolean }>;
    // 050: the browsing display rate published by /cars. `null` when the car has no
    // usable rate card — then no price is shown at all (never a zero).
    pricing?: {
      currency_symbol: string;
      per_hour_minor: number | null;
      per_day_minor: number | null;
      /** 400 (#1354): the rate card's time-of-day promos; absent on older payloads. */
      promos?: Promo[];
    } | null;
    // 057: the browsing rating published by /cars. `null` when nobody has reviewed this
    // car — then no rating is shown at all, never a zero.
    rating?: { average: number; count: number } | null;
    /** 408 (#1434): present only for a car on Cartrack with a reading. */
    fuel?: CarFuel;
  };
}

export function CarCard({ car, searchWindow }: CarCardProps) {
  const primaryImg = car.images?.find((i) => i.is_primary) ?? car.images?.[0];
  const symbol = car.pricing?.currency_symbol;
  // Rentals here are mainly hourly, so the hourly rate leads; a day-only rate card
  // falls back to its daily amount rather than inventing an hourly one.
  const hourly = formatMinor(car.pricing?.per_hour_minor, symbol);
  const daily = formatMinor(car.pricing?.per_day_minor, symbol);
  const rate = hourly
    ? { text: hourly, unit: '/hour' }
    : daily
      ? { text: daily, unit: '/day' }
      : null;
  // 400 (#1354, mock-up screen 1): the lowest promo price and its hours, touching promos joined.
  const chip = promoChip(car.pricing?.promos);

  return (
    <Card
      onClick={() => route(`/cars/${car._id}${tripQueryString(searchWindow ?? {})}`)}
      className="flex flex-col gap-3"
    >
      <div className="aspect-video rounded-lg bg-surface-mid overflow-hidden">
        {primaryImg ? (
          <img
            src={primaryImg.url}
            alt={car.label ?? car.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Car
              data-testid="car-card-fallback-icon"
              size={40}
              className="text-gray-600"
              aria-hidden="true"
            />
          </div>
        )}
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-text truncate">{car.label ?? car.name}</h3>
        <div className="mt-1 flex gap-3 text-xs text-gray-400">
          <span>{car.transmission}</span>
          <span>{car.fuel_type}</span>
          <span>{car.seat_capacity} seats</span>
          {/* 057: one glyph, not five stars — a card this size already carries a price,
              and the detail page is where the full rating and the reviews live. */}
          {car.rating && (
            <span data-testid="car-card-rating" className="flex items-center gap-1">
              <svg width="12" height="12" viewBox="0 0 20 20" fill="#FFD800" aria-hidden="true">
                <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 15l-5.2 2.6 1-5.8L1.5 7.7l5.9-.9L10 1.5z" />
              </svg>
              <span className="font-semibold text-text">{car.rating.average.toFixed(1)}</span>
              <span>({car.rating.count})</span>
            </span>
          )}
          {/* 363 (design 2a): an unrated car ranks mid-list in Recommended, so it says why it is there. */}
          {!car.rating && (
            <span
              data-testid="car-card-new"
              className="rounded-full border border-border px-2 text-[11px] font-semibold text-text-muted"
            >
              New
            </span>
          )}
        </div>
      </div>
      {/* 408 (mock-up screen 1; spec FR-002 adds the time): the fuel, small. */}
      <FuelGauge fuel={car.fuel} />
      {rate && (
        <div className="text-primary font-bold">
          {rate.text}
          <span className="text-xs font-normal text-gray-400">{rate.unit}</span>
        </div>
      )}
      {chip && (
        <span
          data-testid="car-card-promo"
          className="-mt-2 self-start rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary"
        >
          {`Promo ${formatMinor(chip.per_hour_minor, symbol)}/hour · ${chip.hours}`}
        </span>
      )}
    </Card>
  );
}
